import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLingui } from '@lingui/react'
import { Trans } from '@lingui/macro'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Alert from '@mui/material/Alert'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'

import DeviceCard from '@/components/ui/cards/DeviceCard'
import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined'
import { CustomDevice } from '@/core/models/custom-device.model'

type CustomDevicesProps = {
  customDevices: (CustomDevice & { available: boolean })[]
  isDeleting?: boolean
  onDeleteCustomDevice?: (customDeviceId: string) => void
}

export default function CustomDevices(props: CustomDevicesProps) {
  const { customDevices, isDeleting, onDeleteCustomDevice } = props
  const { t } = useTranslation()
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState<boolean>(false)
  const [currentCustomDeviceId, setCurrentCustomDeviceId] = useState<string>()

  const handleOpenDeleteConfirmationDialog = useCallback((id: string) => {
    setOpenDeleteConfirmationDialog(true)
    setCurrentCustomDeviceId(id)
  }, [])

  const handleCloseDeleteConfirmationDialog = useCallback(() => {
    setOpenDeleteConfirmationDialog(false)
  }, [])

  const handleDelete = () => {
    if (!currentCustomDeviceId) return
    onDeleteCustomDevice?.(currentCustomDeviceId)
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 500, lineHeight: '24px', mb: 2 }}>
        {t('page.configuration.devices.title')}
      </Typography>
      <LocalNetworkAccessInstruction />
      <Grid container spacing={4}>
        {customDevices.map((customDevice) => (
          <Grid key={customDevice._id} item xs={12} md={6}>
            <DeviceCard
              id={customDevice._id}
              imgUrl={customDevice.iconURL}
              isAvailable={customDevice.available}
              name={customDevice.customName}
              onDelete={handleOpenDeleteConfirmationDialog}
              type={t(`constants.device.type.${customDevice.type}`, { defaultValue: customDevice.type })}
            />
          </Grid>
        ))}
      </Grid>
      <DeleteConfirmationDialog
        isDeleting={isDeleting}
        open={openDeleteConfirmationDialog}
        onClose={handleCloseDeleteConfirmationDialog}
        onDelete={handleDelete}
        title={t('dialog.deleteConfirmation.customDevice.title')}
        message={t('dialog.deleteConfirmation.customDevice.message')}
      />
    </Box>
  )
}

function LocalNetworkAccessInstruction() {
  useLingui()
  const [open, setOpen] = useState(false)
  return (
    <Box sx={{ mb: 4 }}>
      <Accordion expanded={open} onChange={() => setOpen(!open)}>
        <AccordionSummary
          aria-controls="local-network-access-content"
          id="local-network-access-header"
          sx={{
            minHeight: '0 !important',
            p: 0,
            '& .MuiAccordionSummary-content': {
              margin: 0,
              '&.Mui-expanded': {
                margin: 0,
              },
            },
          }}
        >
          <Alert
            action={open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            severity="warning"
            sx={{ borderRadius: 0, width: '100%', '& .MuiAlert-message': { width: '100%' } }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              <Trans>Chrome blocks local network access by default. Enable it to connect to medical devices.</Trans>
            </Typography>
          </Alert>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            <Trans>To connect to local devices, Chrome requires you to allow Local Network Access for this site. Follow these steps:</Trans>
          </Typography>
          <List
            component="div"
            sx={{
              '& .MuiListItemText-primary': { fontSize: '0.9rem' },
            }}
          >
            <ListItem>
              <ListItemText
                primary={
                  <Trans>
                    1. Click the site information icon <InfoOutlinedIcon /> next to the address bar
                  </Trans>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Trans>
                    2. Open Site settings <SettingsOutlinedIcon />{' '}
                  </Trans>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Trans>
                    3. Under Additional permissions, set Local network access <RouterOutlinedIcon /> to Allow
                  </Trans>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText primary={<Trans>4. Reload the page</Trans>} />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}
